import { useMutation, useQuery } from "@tanstack/react-query";

import { queryClient } from "@/config/react-query";

import { parseError } from "@/lib/parse-error";
import { toast } from "@/lib/use-toast";

import {
  QualiphyApiKeySaveRequest,
  QualiphyApiKeyStatus,
  QualiphyExamListResponse,
  QualiphyGFECreateRequest,
  QualiphyGFEDetail,
  QualiphyPartnerPharmacyListResponse,
  QualiphyTreatmentPackageListResponse,
} from "@/types/qualiphy";

import { requestData } from "@/api/api-config";

// --- API Functions ---

async function getAvailableExams(): Promise<QualiphyExamListResponse | null> {
  return await requestData<QualiphyExamListResponse>(
    "qualiphy/exams",
    "get",
    undefined,
    false,
  );
}

async function getPartnerPharmacies(): Promise<QualiphyPartnerPharmacyListResponse | null> {
  return await requestData<QualiphyPartnerPharmacyListResponse>(
    "qualiphy/partner-pharmacies",
    "get",
    undefined,
    false,
  );
}

async function getTreatmentPackages(
  pharmacyId: number,
  examId: number,
): Promise<QualiphyTreatmentPackageListResponse | null> {
  return await requestData<QualiphyTreatmentPackageListResponse>(
    `qualiphy/treatment-packages?pharmacy_id=${pharmacyId}&exam_id=${examId}`,
    "get",
    undefined,
    false,
  );
}

async function getPatientGFEs(
  patientId: string,
): Promise<QualiphyGFEDetail[] | null> {
  return await requestData<QualiphyGFEDetail[]>(
    `qualiphy/gfe/patient/${patientId}`,
    "get",
    undefined,
    false,
  );
}

async function createGFERequest(
  data: QualiphyGFECreateRequest,
): Promise<QualiphyGFEDetail | null> {
  return await requestData<QualiphyGFEDetail>(
    "qualiphy/gfe",
    "post",
    { json: data },
    false,
  );
}

async function resendGFEInvite(gfeId: string): Promise<unknown> {
  return await requestData(
    "qualiphy/gfe/" + gfeId + "/resend",
    "post",
    undefined,
    false,
  );
}

async function getApiKeyStatus(): Promise<QualiphyApiKeyStatus | null> {
  return await requestData<QualiphyApiKeyStatus>(
    "qualiphy/settings/api-key",
    "get",
    undefined,
    false,
  );
}

async function saveApiKey(
  data: QualiphyApiKeySaveRequest,
): Promise<QualiphyApiKeyStatus | null> {
  return await requestData<QualiphyApiKeyStatus>(
    "qualiphy/settings/api-key",
    "put",
    { json: data },
    false,
  );
}

// --- Query Keys ---

export const qualiphyQueryKeys = {
  all: ["qualiphy"] as const,
  exams: () => [...qualiphyQueryKeys.all, "exams"] as const,
  partnerPharmacies: () =>
    [...qualiphyQueryKeys.all, "partner-pharmacies"] as const,
  treatmentPackages: (pharmacyId: number, examId: number) =>
    [
      ...qualiphyQueryKeys.all,
      "treatment-packages",
      pharmacyId,
      examId,
    ] as const,
  patientGfes: (patientId: string) =>
    [...qualiphyQueryKeys.all, "gfe", "patient", patientId] as const,
  apiKeyStatus: () => [...qualiphyQueryKeys.all, "api-key-status"] as const,
};

// --- Query Options ---

export const qualiphyQueryOptions = {
  exams: () => ({
    queryKey: qualiphyQueryKeys.exams(),
    queryFn: getAvailableExams,
    select: (data: QualiphyExamListResponse | null) => data?.exams ?? [],
  }),
  partnerPharmacies: () => ({
    queryKey: qualiphyQueryKeys.partnerPharmacies(),
    queryFn: getPartnerPharmacies,
    select: (data: QualiphyPartnerPharmacyListResponse | null) =>
      data?.pharmacies ?? [],
  }),
  treatmentPackages: (pharmacyId: number, examId: number) => ({
    queryKey: qualiphyQueryKeys.treatmentPackages(pharmacyId, examId),
    queryFn: () => getTreatmentPackages(pharmacyId, examId),
    select: (data: QualiphyTreatmentPackageListResponse | null) =>
      data?.treatmentPackages ?? [],
  }),
  patientGfes: (patientId: string) => ({
    queryKey: qualiphyQueryKeys.patientGfes(patientId),
    queryFn: () => getPatientGFEs(patientId),
    select: (data: QualiphyGFEDetail[] | null) => data ?? [],
  }),
  apiKeyStatus: () => ({
    queryKey: qualiphyQueryKeys.apiKeyStatus(),
    queryFn: getApiKeyStatus,
    select: (data: QualiphyApiKeyStatus | null) => data?.configured ?? false,
  }),
};

// --- Hooks ---

interface UseQualiphyExamCatalogOptions {
  enabled: boolean;
}

/**
 * Loads the tenant's Qualiphy configuration status before attempting to load
 * the remote exam catalog. This allows tenants to keep the module enabled
 * while they finish setting up their Qualiphy account and API key.
 */
export function useQualiphyExamCatalog({
  enabled,
}: UseQualiphyExamCatalogOptions) {
  const configurationQuery = useQuery({
    ...qualiphyQueryOptions.apiKeyStatus(),
    enabled,
  });
  const isConfigured = configurationQuery.data === true;

  const examsQuery = useQuery({
    ...qualiphyQueryOptions.exams(),
    enabled: enabled && isConfigured,
  });

  return {
    exams: examsQuery.data ?? [],
    isConfigured,
    isConfigurationLoading: enabled && configurationQuery.isLoading,
    isExamsLoading: enabled && isConfigured && examsQuery.isLoading,
  };
}

// --- Mutations ---

export const useCreateQualiphyExamMutation = () => {
  return useMutation<QualiphyGFEDetail | null, Error, QualiphyGFECreateRequest>(
    {
      mutationFn: createGFERequest,
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: qualiphyQueryKeys.patientGfes(variables.patientId),
        });
      },
      onError: async (error: any) => {
        const errorMessage = await parseError(
          error,
          "Failed to create Qualiphy exam invite. Please try again.",
        );
        toast({
          title: "Error",
          description: errorMessage,
          variant: "error",
        });
      },
    },
  );
};

export const useResendQualiphyExamMutation = () => {
  return useMutation<unknown, Error, { gfeId: string; patientId: string }>({
    mutationFn: ({ gfeId }) => resendGFEInvite(gfeId),
    onSuccess: () => {
      toast({
        title: "Invite Resent",
        description: "The exam invite has been resent to the patient.",
        variant: "success",
      });
    },
    onError: async (error: any) => {
      const errorMessage = await parseError(
        error,
        "Failed to resend exam invite. Please try again.",
      );
      toast({
        title: "Error",
        description: errorMessage,
        variant: "error",
      });
    },
  });
};

export const useSaveQualiphyApiKeyMutation = () => {
  return useMutation<QualiphyApiKeyStatus | null, Error, string>({
    mutationFn: (apiKey) => saveApiKey({ apiKey }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qualiphyQueryKeys.apiKeyStatus(),
      });
    },
    onError: async (error: any) => {
      const errorMessage = await parseError(
        error,
        "Failed to save API key. Please check the key and try again.",
      );
      toast({
        title: "Error",
        description: errorMessage,
        variant: "error",
      });
    },
  });
};
